import { useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  RefreshCw,
  AlertCircle,
} from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import StatusChip from '../../../../components/common/StatusChip';
import { formatCurrency } from '../../utils';
import type { MasterTrade, ChildTrade } from '../../types';

interface MasterChildTradeViewerProps {
  masterTrade: MasterTrade;
  childTrades: ChildTrade[];
  onRetryChildTrade?: (childTradeId: string) => void;
}

export default function MasterChildTradeViewer({
  masterTrade,
  childTrades,
  onRetryChildTrade,
}: MasterChildTradeViewerProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden shadow-sm mb-3">
      {/* Master Row Header */}
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-3 cursor-pointer hover:bg-muted/30 transition-colors"
      >
        <div className="flex items-center space-x-3">
          <button className="text-muted-foreground p-1 hover:text-foreground">
            {isExpanded ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </button>
          <div>
            <div className="flex items-center space-x-2">
              <span className="font-mono text-xs font-bold text-foreground">
                #{masterTrade.ticket}
              </span>
              <span
                className={`px-2 py-0.5 text-[10px] font-bold rounded uppercase ${
                  masterTrade.direction === 'buy'
                    ? 'bg-emerald-100 text-emerald-800'
                    : 'bg-rose-100 text-rose-800'
                }`}
              >
                {masterTrade.direction}
              </span>
              <span className="text-xs font-semibold text-foreground">
                {masterTrade.symbol}
              </span>
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Manager: {masterTrade.managerName} | Group:{' '}
              {masterTrade.groupName}
            </p>
          </div>
        </div>

        {/* Trade Details Summary */}
        <div className="flex flex-wrap items-center gap-3 sm:gap-6 text-xs">
          <div>
            <p className="text-[10px] text-muted-foreground">Volume</p>
            <p className="font-bold text-foreground">
              {masterTrade.masterVolume} Lots
            </p>
          </div>
          <div>
            <p className="text-[10px] text-muted-foreground">Open Price</p>
            <p className="font-semibold text-foreground">
              {masterTrade.openPrice}
            </p>
          </div>
          <div>
            <p className="text-[10px] text-muted-foreground">Profit/Loss</p>
            <p
              className={`font-bold ${masterTrade.profit >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}
            >
              {formatCurrency(masterTrade.profit)}
            </p>
          </div>
          <div>
            <p className="text-[10px] text-muted-foreground">Exec. Time</p>
            <p className="font-mono text-foreground">
              {masterTrade.executionTimeMs} ms
            </p>
          </div>
          <StatusChip status={masterTrade.executionStatus} size="sm" />
        </div>
      </div>

      {/* Child Trades Expandable Table */}
      {isExpanded && (
        <div className="border-t border-border bg-muted/20 p-4 space-y-3">
          <div className="flex items-center justify-between text-xs font-semibold text-muted-foreground">
            <span>
              Allocated Child Executions ({childTrades.length} Sub-accounts)
            </span>
            {masterTrade.failedChildCount > 0 && (
              <span className="text-rose-600 font-bold flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5" />
                {masterTrade.failedChildCount} Executions Failed
              </span>
            )}
          </div>

          <div className="overflow-x-auto rounded-lg border border-border bg-card">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-muted-foreground text-[10px]">
                  <th className="px-3 py-2 text-left">Child Ticket</th>
                  <th className="px-3 py-2 text-left">Account</th>
                  <th className="px-3 py-2 text-right">Allocated Lots</th>
                  <th className="px-3 py-2 text-right">Price</th>
                  <th className="px-3 py-2 text-right">P/L</th>
                  <th className="px-3 py-2 text-center">Status</th>
                  <th className="px-3 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {childTrades.map(child => (
                  <tr key={child.id} className="hover:bg-muted/20">
                    <td className="px-3 py-2 font-mono text-[11px] font-medium">
                      {child.ticket}
                    </td>
                    <td className="px-3 py-2">
                      <p className="font-semibold text-foreground">
                        {child.clientName}
                      </p>
                      <p className="text-[10px] text-muted-foreground">
                        {child.accountNumber}
                      </p>
                    </td>
                    <td className="px-3 py-2 text-right font-bold">
                      {child.allocatedVolume}
                    </td>
                    <td className="px-3 py-2 text-right">{child.openPrice}</td>
                    <td
                      className={`px-3 py-2 text-right font-semibold ${child.profit >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}
                    >
                      {formatCurrency(child.profit)}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <StatusChip status={child.executionStatus} size="sm" />
                    </td>
                    <td className="px-3 py-2 text-right">
                      {child.executionStatus === 'failed' &&
                        onRetryChildTrade && (
                          <Button
                            size="sm"
                            variant="destructive"
                            className="h-6 text-[10px] px-2"
                            onClick={() => onRetryChildTrade(child.id)}
                          >
                            <RefreshCw className="w-3 h-3 mr-1" />
                            Retry
                          </Button>
                        )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
